import { resolveAnalyticsClientSurface } from '@/utils/analytics-device';

/** 单条操作日志 schema 版本（上报 / 检索用） */
export const ANALYTICS_LOG_SCHEMA_VERSION = 1;

export function createAnalyticsId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `e-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * 转成 IndexedDB / structuredClone 可写入的纯 JSON 值。
 * 去掉 Vue/Pinia Proxy、函数、循环引用等不可克隆内容。
 * @param {unknown} value
 * @returns {unknown}
 */
export function cloneForIdb(value) {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
}

/**
 * 从业务 payload 中抽出 funnelSessionId，避免与顶层字段重复。
 * props 仅保留 JSON 可序列化摘要（金额/选项等标量），不落整包业务对象。
 * @param {unknown} raw
 * @returns {{ props: unknown, funnelSessionId?: string }}
 */
export function splitFunnelFromProps(raw) {
  if (raw == null) return { props: null };
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return { props: cloneForIdb(raw) };
  }
  const props = { ...raw };
  const funnelSessionId =
    props.funnelSessionId != null && String(props.funnelSessionId).trim()
      ? String(props.funnelSessionId).trim()
      : undefined;
  if ('funnelSessionId' in props) delete props.funnelSessionId;
  const plain = cloneForIdb(props);
  if (!plain || typeof plain !== 'object' || Array.isArray(plain)) {
    return { props: plain ?? null, funnelSessionId };
  }
  return {
    props: Object.keys(plain).length ? plain : null,
    funnelSessionId,
  };
}

/**
 * 组装标准 log 行（控制台 / 持久化 / 日后上报同一形状）。
 *
 * @param {{
 *   key: string,
 *   label?: string,
 *   value?: unknown,
 *   props?: unknown,
 *   funnelSessionId?: string,
 *   durationMs?: number,
 *   at?: number,
 *   id?: string,
 *   platform?: string,
 *   ua?: string,
 *   viewportW?: number,
 * }} entry
 * @param {{
 *   deviceId?: string,
 *   userId?: string,
 *   routeName?: string,
 *   platform?: string,
 *   ua?: string,
 *   viewportW?: number,
 *   resolveSurface?: boolean,
 * }} ctx
 */
export function buildAnalyticsLogRow(entry, ctx = {}) {
  const key = String(entry?.key ?? '').trim();
  if (!key) return null;

  const rawProps = entry?.props !== undefined ? entry.props : entry?.value;
  const split = splitFunnelFromProps(rawProps);
  const funnelSessionId =
    (entry?.funnelSessionId != null && String(entry.funnelSessionId).trim()) ||
    split.funnelSessionId ||
    undefined;

  const surface =
    ctx.resolveSurface === false
      ? {
          platform: entry?.platform || ctx.platform,
          ua: entry?.ua || ctx.ua,
          viewportW: entry?.viewportW ?? ctx.viewportW,
        }
      : resolveAnalyticsClientSurface();

  const platform =
    (entry?.platform && String(entry.platform)) ||
    (ctx.platform && String(ctx.platform)) ||
    surface.platform ||
    undefined;
  const ua =
    (entry?.ua && String(entry.ua)) ||
    (ctx.ua && String(ctx.ua)) ||
    surface.ua ||
    undefined;
  const viewportW =
    entry?.viewportW != null
      ? Number(entry.viewportW)
      : ctx.viewportW != null
        ? Number(ctx.viewportW)
        : surface.viewportW;

  /** @type {Record<string, unknown>} */
  const row = {
    v: ANALYTICS_LOG_SCHEMA_VERSION,
    id: (entry?.id && String(entry.id)) || createAnalyticsId(),
    at: Number.isFinite(Number(entry?.at)) ? Number(entry.at) : Date.now(),
    key,
    label: String(entry?.label ?? key).trim() || key,
    deviceId: ctx.deviceId || undefined,
    userId: ctx.userId || undefined,
    routeName: ctx.routeName || undefined,
    platform: platform === 'h5' || platform === 'pc' ? platform : undefined,
    ua: ua === 'mobile' || ua === 'desktop' ? ua : undefined,
    props: split.props,
  };

  if (Number.isFinite(viewportW) && viewportW > 0) {
    row.viewportW = Math.round(viewportW);
  }
  if (funnelSessionId) row.funnelSessionId = funnelSessionId;

  const durationMs = entry?.durationMs;
  if (durationMs != null && Number.isFinite(Number(durationMs))) {
    row.durationMs = Math.max(0, Math.round(Number(durationMs)));
  }

  return row;
}

/**
 * 把旧版 `{ value, ... }` 或残缺行规范成当前 schema。
 * @param {unknown} row
 */
export function normalizeAnalyticsLogRow(row) {
  if (!row || typeof row !== 'object') return null;
  const r = /** @type {Record<string, unknown>} */ (row);
  if (
    r.v === ANALYTICS_LOG_SCHEMA_VERSION &&
    typeof r.id === 'string' &&
    typeof r.key === 'string' &&
    'props' in r
  ) {
    return r;
  }
  return buildAnalyticsLogRow(
    {
      id: typeof r.id === 'string' ? r.id : undefined,
      at: typeof r.at === 'number' ? r.at : undefined,
      key: String(r.key ?? ''),
      label: typeof r.label === 'string' ? r.label : undefined,
      value: r.value !== undefined ? r.value : r.props,
      funnelSessionId: typeof r.funnelSessionId === 'string' ? r.funnelSessionId : undefined,
      durationMs: typeof r.durationMs === 'number' ? r.durationMs : undefined,
      platform: typeof r.platform === 'string' ? r.platform : undefined,
      ua: typeof r.ua === 'string' ? r.ua : undefined,
      viewportW: typeof r.viewportW === 'number' ? r.viewportW : undefined,
    },
    {
      deviceId: typeof r.deviceId === 'string' ? r.deviceId : undefined,
      userId: typeof r.userId === 'string' ? r.userId : undefined,
      routeName: typeof r.routeName === 'string' ? r.routeName : undefined,
      resolveSurface: false,
    }
  );
}
